import {
  Box,
  Flex,
  Image,
  InputGroup,
  InputRightElement,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import SearchableSelect from "../../components/selectForm";
import calendarIcon from "../../assets/images/onleave/calendar.svg";
import ReactApexChart from "react-apexcharts";
import { useChart } from "../../hooks/useChart";
import { useSalesManagementDetail } from "../../hooks/useSalesManagement";
import { formatTableValue } from "./formatTable";
import { useCategories } from "../../categorys/SalesManagement/statement";

const TableStatement = () => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [employeeId, setEmployeeId] = useState(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  const { commissionAnalystData, refetch, isLoading, isRefetching } =
    useSalesManagementDetail("", startDate, endDate, employeeId);

  const {
    periodData,
    dataSales,
    // refetch,
    // isLoading,
    // isRefetching,
  } = useChart(employeeId, startDate, endDate);

  const optionsPeriod = {
    chart: {
      type: "bar",
      zoom: {
        enabled: false,
      },
    },
    title: {
      text: "Biểu đồ số giao dịch",
      align: "left",
      style: {
        fontSize: "14px",
        fontFamily: "var(--font-heading-main)",
        fontWeight: "500",
        width: "100%",
      },
    },
    xaxis: {
      type: "text",
      categories: [
        "T1",
        "T2",
        "T3",
        "T4",
        "T5",
        "T6",
        "T7",
        "T8",
        "T9",
        "T10",
        "T11",
        "T12",
      ],
      title: {
        // text: "Ngày",
        style: {
          fontSize: "10px",
          fontFamily: "var(--font-heading-main)",
          fontWeight: "400",
        },
      },
    },
    yaxis: {
      title: {
        // text: "Lợi nhuận (ETH)",
        style: {
          fontSize: "10px",
          fontFamily: "var(--font-heading-main)",
          fontWeight: "400",
        },
      },
      forceNiceScale: true,
      labels: {
        formatter: (value) => value.toFixed(0).toLocaleString(), // Làm tròn về số nguyên
      },
    },
  };

  const seriesPeriod = [
    {
      name: "Số giao dịch",
      data: Array.isArray(periodData) ? periodData : [],
    },
  ];

  const optionsSales = {
    chart: {
      type: "bar",
      zoom: {
        enabled: false,
      },
    },
    title: {
      text: "Biểu đồ doanh số",
      align: "left",
      style: {
        fontSize: "14px",
        fontFamily: "var(--font-heading-main)",
        fontWeight: "500",
        width: "100%",
      },
    },
    xaxis: {
      type: "text",
      categories: [
        "T1",
        "T2",
        "T3",
        "T4",
        "T5",
        "T6",
        "T7",
        "T8",
        "T9",
        "T10",
        "T11",
        "T12",
      ],
      title: {
        // text: "Ngày",
        style: {
          fontSize: "10px",
          fontFamily: "var(--font-heading-main)",
          fontWeight: "400",
        },
      },
    },
    yaxis: {
      title: {
        // text: "Lợi nhuận (ETH)",
        style: {
          fontSize: "10px",
          fontFamily: "var(--font-heading-main)",
          fontWeight: "400",
        },
      },
      labels: {
        formatter: (value) => value.toLocaleString() + " tỷ",
      },
    },
  };

  const seriesSales = [
    {
      name: "Doanh số",
      data: Array.isArray(dataSales) ? dataSales : [],
    },
  ];

  const tableData = {
    headers: [
      {
        label: "Thời gian",
        key: "bookingTime",
      },
      // {
      //   label: "Số HĐ",
      //   key: "periodReceive",
      // },
      {
        label: "Dự án",
        key: "project",
      },
      {
        label: "Mã căn",
        key: "codeApartment",
      },
      {
        label: "Giá trị niêm yết",
        key: "totalValue",
      },
      {
        label: "Tổng phí dịch vụ",
        key: "priceForCommission",
      },
      {
        label: "Tổng PDV đã thanh toán",
        key: "amountReceive",
      },
    ],
    data: commissionAnalystData,
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldFetch(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const { constantData } = useCategories(shouldFetch);

  const formatEntries = (entries) =>
    Object.entries(entries || {}).map(([key, { fullName, code }]) => ({
      key: key, // _id
      value: fullName, // Hiển thị tên nhân viên
      code: code,
    }));

  const employees = useMemo(() => {
    if (!constantData?.employee) return [];

    return Object.entries(constantData.employee).map(
      ([id, { fullName, code }]) => ({
        key: id,
        value: `${fullName} (${code})`,
      }),
    );
  }, [constantData?.employee]);

  return (
    <>
      <Flex flexDirection={"column"} gap={{ base: "16px" }}>
        <Flex
          backgroundColor="var(--color-main)"
          borderRadius={{ base: "12px" }}
          flexDirection={"column"}
        >
          <Box p={"16px 0px 12px 0px"}>
            <Text
              fontSize={{ base: "20px" }}
              fontWeight={600}
              p={"0px 16px 0px 16px"}
            >
              Báo cáo doanh số của nhân viên
            </Text>
            <Flex
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={{ base: "32px" }}
              mt={"20px"}
            >
              <Flex
                gap={{ base: "16px" }}
                flex={"1"}
                flexDirection={{ base: "column" }}
              >
                <Flex
                  alignItems={"center"}
                  gap={{ base: "16px" }}
                  p={"0px 16px 0px 16px"}
                >
                  <SearchableSelect
                    isShowSearch={true}
                    options={employees}
                    initialSelectedOption={{ key: 0, value: "Chọn nhân viên" }}
                    setValue={setEmployeeId}
                  />
                  <InputGroup w={"100%"} maxW={"255px"} zIndex={1000}>
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      dateFormat="dd/MM/yyyy"
                      locale="vi"
                      onChange={(update) => {
                        setDateRange(update);
                      }}
                      isClearable={true}
                      placeholderText="Từ ngày - Đến ngày"
                      className="datePickerStyle"
                    />
                    {!startDate && !endDate && (
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    )}
                  </InputGroup>
                </Flex>
                <Flex gap={"17px"}>
                  <Box flex={1}>
                    <ReactApexChart
                      options={optionsPeriod}
                      series={seriesPeriod}
                      type="bar"
                    />
                  </Box>
                  <Box flex={1}>
                    <ReactApexChart
                      options={optionsSales}
                      series={seriesSales}
                      type="bar"
                    />
                  </Box>
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      </Flex>
      {employeeId ? (
        <Box
          p={"16px"}
          border={"1px solid var(--color-black-2)"}
          bg={"var(--color-main)"}
          borderRadius={"8px"}
          mt={"16px"}
        >
          <Text fontSize={{ base: "14px" }} fontWeight={500} mb={"16px"}>
            Bảng thống kê chi tiết
          </Text>
          <Box
            width={"100%"}
            height={"100%"}
            maxH={"430px"}
            overflowY={"auto"}
            sx={{
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                width: "4px",
                borderRadius: "50px",
                background: "var(--color-background)",
              },
              "&::-moz-scrollbar": {
                width: "4px",
              },
              "&::-moz-scrollbar-thumb": {
                width: "4px",
                borderRadius: "50px",
                background: "var(--color-background)",
              },
            }}
          >
            <Skeleton isLoaded={!isLoading && !isRefetching}>
              <TableContainer
                w={"100%"}
                maxH="calc(100vh - 350.5px)"
                overflowY="auto"
              >
                <Table w={"100%"} variant="unstyled">
                  <Thead
                    position="sticky"
                    top="0"
                    zIndex="1"
                    w={"100%"}
                    h={"41px"}
                    color="white"
                    fontWeight="400"
                    backgroundColor="#F9FAFD"
                  >
                    <Tr h={{ base: "41px" }}>
                      {tableData.headers.map((e, index) => {
                        let width = "auto";
                        let textAlign = "auto";
                        if (e.key === "") {
                          width = "2%";
                          textAlign = "center";
                        } else if (e.key === "codeApartment") {
                          width = "5%";
                          textAlign = "start";
                        } else if (e.key === "bookingTime") {
                          width = "10%";
                          textAlign = "start";
                        } else if (e.key === "project") {
                          width = "10%";
                          textAlign = "start";
                        } else if (e.key === "periodReceive") {
                          width = "10%";
                          textAlign = "start";
                        } else if (e.key === "totalValue") {
                          width = "10%";
                          textAlign = "start";
                        } else if (e.key === "priceForCommission") {
                          width = "10%";
                          textAlign = "start";
                        } else if (
                          e.key === "timeDelivery" ||
                          e.key === "timeStart" ||
                          e.key === "timeEnd"
                        ) {
                          width = "5%";
                          textAlign = "end";
                        } else {
                          width = "10%";
                          textAlign = "end";
                        }
                        return (
                          <Td
                            p={"16px 16px 8px 16px"}
                            key={index}
                            border={"none"}
                            color={"#51607B"}
                            fontSize={{ base: "14px" }}
                            w={width}
                          >
                            <Box textAlign={"center"} fontWeight={"bold"}>{e.label}</Box>
                          </Td>
                        );
                      })}
                    </Tr>
                  </Thead>
                  <Tbody w={"100%"} h={"100%"}>
                    {tableData.data?.length > 0 ? (
                      tableData.data?.map((e, rowIndex) => {
                        const items = { ...e };
                        delete items.id;
                        delete items.edit;
                        delete items.detail;
                        const keyValues = Object.keys(items);
                        return (
                          <Tr key={rowIndex} h={"72px"}>
                            {keyValues.map((keyvalue, index) => {
                              let width = "auto";
                              let widthText = "100%";
                              let textAlign = "auto";
                              let whiteSpace = "inherit";
                              if (keyvalue === "") {
                                width = "2%";
                                textAlign = "center";
                              } else if (keyvalue === "codeApartment") {
                                width = "5%";
                                textAlign = "start";
                              } else if (keyvalue === "bookingTime") {
                                width = "10%";
                                textAlign = "start";
                              } else if (keyvalue === "project") {
                                width = "10%";
                                textAlign = "start";
                              } else if (keyvalue === "periodReceive") {
                                width = "10%";
                                textAlign = "start";
                              } else if (keyvalue === "totalValue") {
                                width = "10%";
                                textAlign = "start";
                              } else if (keyvalue === "priceForCommission") {
                                width = "10%";
                                textAlign = "start";
                              } else if (keyvalue === "amountPay") {
                                width = "10%";
                                textAlign = "start";
                              } else {
                                width = "10%";
                                textAlign = "end";
                                whiteSpace = "inherit";
                              }
                              return (
                                <Td
                                  p={"16px 16px 8px 16px"}
                                  w={width}
                                  key={index}
                                >
                                  <Box
                                    fontSize={{ base: "14px" }}
                                    lineHeight={"19.6px"}
                                    textAlign={"start"}
                                    fontWeight={500}
                                    color={"var(--color-info-employee)"}
                                    w={widthText}
                                    whiteSpace={whiteSpace}
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    display="-webkit-box"
                                    sx={{
                                      WebkitLineClamp: "2",
                                      WebkitBoxOrient: "vertical",
                                    }}
                                  >
                                    {formatTableValue(e[keyvalue], keyvalue)}
                                  </Box>
                                </Td>
                              );
                            })}
                          </Tr>
                        );
                      })
                    ) : (
                      <Tr>
                        <Td
                          colSpan={tableData.headers.length}
                          textAlign="center"
                          padding={"40px 0"}
                        >
                          Không có dữ liệu
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Skeleton>
          </Box>
        </Box>
      ) : (
        ""
      )}
    </>
  );
};

export default TableStatement;
